import { forwardRef, useRef, type InputHTMLAttributes } from "react";
import { cn } from "../../lib/utils";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, onWheel, onFocus, onMouseUp, ...props }, ref) => {
    // 這次 focus 之後還沒被滑鼠動過 —— 用來保住下面 onFocus 做的全選（見 onMouseUp）。
    const pristine = useRef(false);

    return (
      <input
        type={type}
        ref={ref}
        onWheel={(e) => {
          // type="number" 在 Chrome／Edge 只要有焦點，滑鼠滾輪就會直接加減數值，而且不捲動頁面。
          // 使用者以為在捲頁面，其實在改數字 —— 訂單 ORD260820ZU823K 的人數 3 就是這樣變成 2 的
          // （2026-08-22 已用 Playwright 重現）。全站 19 個數字欄位同樣受影響，含計價規則、
          // 路線價格、匯率與信用額度等金額欄位，風險更高，因此統一在共用元件擋掉。
          // 失焦後滾輪回到「捲動頁面」的預設行為，數值不再被改動。
          if (type === "number") e.currentTarget.blur();
          onWheel?.(e);
        }}
        onFocus={(e) => {
          // 數字欄位幾乎都帶著預設值（新增路線價格的 0、人數的 1、匯率的舊值）。
          // 聚焦時把整段選起來，直接打字就覆蓋，不必先把游標移到數字後面逐字刪
          // （2026-09-21 客戶反饋：新增路線價格的「價格 (HKD)」要人手刪掉 0）。
          // 只挑 type="number"：文字欄位（姓名、地址）多半是要接著補字，全選反而礙事。
          if (type === "number") {
            e.currentTarget.select();
            pristine.current = true;
          }
          onFocus?.(e);
        }}
        onMouseUp={(e) => {
          // 瀏覽器會在 mouseup 把游標放到點擊處，順手取消上面 select() 的選取，
          // 全選就白做了。所以只擋掉「focus 之後第一次點擊」的預設行為，選取才留得住。
          // 為什麼不是判斷「這次點擊是否帶來 focus」：dialog 打開時 Radix 會自動把焦點
          // 放在第一個欄位（匯率表就是如此），使用者接著點的那一下並沒有觸發 focus，
          // 用焦點來源判斷會漏掉這個情況。之後在欄位內的點擊不受影響，游標照樣能定位。
          if (pristine.current) {
            pristine.current = false;
            e.preventDefault();
          }
          onMouseUp?.(e);
        }}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-[0_1px_0_oklch(0%_0_0/0.02)_inset]",
          "transition-shadow placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/30",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "file:border-0 file:bg-transparent file:text-sm file:font-medium",
          className,
        )}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export const Select = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, children, ...props }, ref) => (
  <select
    ref={ref}
    className={cn(
      "flex h-9 w-full appearance-none rounded-md border border-input bg-background bg-[length:14px] bg-[right_0.625rem_center] bg-no-repeat pl-3 pr-8 text-sm shadow-[0_1px_0_oklch(0%_0_0/0.02)_inset]",
      "transition-shadow focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/30",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "[background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%2399a%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22><polyline points=%226 9 12 15 18 9%22/></svg>')]",
      className,
    )}
    {...props}
  >
    {children}
  </select>
));
Select.displayName = "Select";
